/**
 * Hex-Koordinaten.
 *
 * Repräsentation: **axial** `{ q, r }`. Die dritte Cube-Komponente ist immer
 * `s = -q - r` und wird dort berechnet, wo sie gebraucht wird (`hexS`).
 *
 * Warum nur eine Repräsentation: zwei parallele Formen (cube intern, axial im
 * Wire-Format) bedeuten Konvertierungen an jeder Grenze und damit eine Klasse von
 * Fehlern, die sich mit `s = -q - r` komplett vermeiden lässt. Die Cube-Vorteile
 * (Distanz, Rotation) sind als Einzeiler verfügbar. Begründung in
 * `docs/ARCHITECTURE.md` E15.
 *
 * Dieses Modul ist pur: keine Zeit, kein Zufall, kein Zustand, keine Pixel.
 */
/**
 * Die sechs geraden Bewegungsrichtungen, in kanonischer Reihenfolge
 * (pointy-top, gegen den Uhrzeigersinn in Hex-Koordinaten).
 *
 * Die Reihenfolge ist Teil des Vertrags: `hexRing` und die Sektorberechnung in
 * `board.ts` leiten daraus die Eckfelder ab und müssen die Reihenfolge der
 * Referenzgrafik reproduzieren.
 */
export const HEX_DIRECTIONS = [
    { q: 1, r: 0 },
    { q: 1, r: -1 },
    { q: 0, r: -1 },
    { q: -1, r: 0 },
    { q: -1, r: 1 },
    { q: 0, r: 1 },
];
/**
 * Wandelt die negative Null in die positive um.
 *
 * `-0 === 0` ist in JavaScript `true`, `Object.is(-0, 0)` aber `false`. Eine
 * negative Null würde deshalb erst sehr spät auffallen: in Vergleichen zweier
 * Zustände, in serialisierten GameEvents oder in Snapshot-Tests. Jeder
 * Koordinatenwert läuft deshalb durch diese Funktion.
 */
function withoutNegativeZero(value) {
    return value === 0 ? 0 : value;
}
/** Der einzige Konstruktor für Hex-Koordinaten. Alle Operationen laufen darüber. */
export function hex(q, r) {
    return { q: withoutNegativeZero(q), r: withoutNegativeZero(r) };
}
/** Die dritte Cube-Komponente. `q + r + s` ist immer 0. */
export function hexS(a) {
    return withoutNegativeZero(-a.q - a.r);
}
export function hexEquals(a, b) {
    return a.q === b.q && a.r === b.r;
}
export function hexAdd(a, b) {
    return hex(a.q + b.q, a.r + b.r);
}
export function hexSubtract(a, b) {
    return hex(a.q - b.q, a.r - b.r);
}
export function hexScale(a, factor) {
    return hex(a.q * factor, a.r * factor);
}
/** Anzahl der geraden Schritte von `a` nach `b`. */
export function hexDistance(a, b) {
    const difference = hexSubtract(a, b);
    return Math.max(Math.abs(difference.q), Math.abs(difference.r), Math.abs(hexS(difference)));
}
/** Nachbarfeld in einer der sechs Richtungen, indiziert wie `HEX_DIRECTIONS`. */
export function hexNeighbor(a, direction) {
    const step = HEX_DIRECTIONS[direction];
    if (step === undefined) {
        throw new RangeError(`Richtungsindex ${direction} liegt außerhalb von 0..5`);
    }
    return hexAdd(a, step);
}
export function hexNeighbors(a) {
    return HEX_DIRECTIONS.map((step) => hexAdd(a, step));
}
/** Stabiler Schlüssel für Maps und Mengen. Kein Anzeigeformat. */
export function hexKey(a) {
    return `${a.q},${a.r}`;
}
/**
 * Alle Felder auf genau `radius` Schritten Abstand vom Zentrum, als geschlossener
 * Rundgang: aufeinanderfolgende Einträge sind Nachbarn, der letzte ist Nachbar des
 * ersten.
 *
 * Der Rundgang beginnt an der Ecke `radius * HEX_DIRECTIONS[4]` und läuft in
 * Richtung `HEX_DIRECTIONS[0]` — dieselbe Reihenfolge, nach der die Referenzgrafik
 * ihre Sektoren aufteilt.
 */
export function hexRing(radius) {
    if (!Number.isInteger(radius) || radius < 0) {
        throw new RangeError(`Radius muss eine nichtnegative ganze Zahl sein, war ${radius}`);
    }
    if (radius === 0)
        return [hex(0, 0)];
    const cells = [];
    let current = hexScale(HEX_DIRECTIONS[4], radius);
    for (let direction = 0; direction < 6; direction++) {
        for (let step = 0; step < radius; step++) {
            cells.push(current);
            current = hexNeighbor(current, direction);
        }
    }
    return cells;
}
//# sourceMappingURL=hex.js.map